import { memo, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useSaveManualSim, type ManualSimInput } from "@/hooks/useSims";
import type { SimCard } from "@/lib/sims";

type ManualStatus = NonNullable<ManualSimInput["status"]>;

interface ManualSimDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sim?: SimCard | null;
}

/** Create/edit a manually managed KPN SIM (KPN is not in Simetric). */
export const ManualSimDialog = memo(function ManualSimDialog({
  open,
  onOpenChange,
  sim,
}: ManualSimDialogProps) {
  const save = useSaveManualSim();
  const [form, setForm] = useState({
    iccid: "",
    msisdn: "",
    imsi: "",
    label: "",
    status: "ACTIVATED" as ManualStatus,
  });

  useEffect(() => {
    if (!open) return;
    setForm({
      iccid: sim?.iccid ?? "",
      msisdn: sim?.msisdn ?? "",
      imsi: sim?.imsi ?? "",
      label: sim?.label ?? "",
      status: (["ACTIVATED", "DEACTIVATED", "INVENTORY"].includes(
        sim?.status ?? "",
      )
        ? sim?.status
        : "ACTIVATED") as ManualStatus,
    });
  }, [open, sim]);

  const iccidValid = /^[0-9A-Za-z]{10,22}$/.test(form.iccid.trim());

  const submit = () => {
    save.mutate(
      {
        id: sim?.id,
        iccid: form.iccid.trim(),
        msisdn: form.msisdn.trim() || null,
        imsi: form.imsi.trim() || null,
        label: form.label.trim() || null,
        status: form.status,
      },
      { onSuccess: () => onOpenChange(false) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="manual-sim-dialog">
        <DialogHeader>
          <DialogTitle>{sim ? "Edit KPN SIM" : "Add KPN SIM"}</DialogTitle>
          <DialogDescription>
            KPN SIMs are not managed through Simetric. Status is tracked
            manually.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="manual-sim-iccid">ICCID</Label>
            <Input
              id="manual-sim-iccid"
              data-testid="manual-sim-iccid"
              value={form.iccid}
              onChange={(e) => setForm({ ...form, iccid: e.target.value })}
              className="font-mono"
            />
            {form.iccid && !iccidValid && (
              <p className="text-xs text-destructive">
                10–22 letters or digits.
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="manual-sim-msisdn">MSISDN</Label>
              <Input
                id="manual-sim-msisdn"
                data-testid="manual-sim-msisdn"
                value={form.msisdn}
                onChange={(e) => setForm({ ...form, msisdn: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="manual-sim-imsi">IMSI</Label>
              <Input
                id="manual-sim-imsi"
                value={form.imsi}
                onChange={(e) => setForm({ ...form, imsi: e.target.value })}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select
              value={form.status}
              onValueChange={(value) =>
                setForm({ ...form, status: value as ManualStatus })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ACTIVATED">Active</SelectItem>
                <SelectItem value="DEACTIVATED">Deactivated</SelectItem>
                <SelectItem value="INVENTORY">Inventory</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="manual-sim-label">Note</Label>
            <Input
              id="manual-sim-label"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            data-testid="save-manual-sim"
            onClick={submit}
            disabled={!iccidValid || save.isPending}
          >
            {save.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
